import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import EditOptionDialog from "../Dialogs/EditOptionDialog";
import DeleteConfirmationDialog from "../Dialogs/DeleteConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import {
  DropDownCategoryEnumModel,
  ILookupItemModel,
  IPagedResultOfLookupItemModel,
  ReferrerTypeEnumModel,
} from "../../types/types";
import { AppDispatch } from "../../store/store";
import {
  addCategoryVariable,
  deleteCategoryVariable,
  searchClients,
  searchReferrers,
  searchVendors,
  updateCategoryVariable,
} from "../../store/modules/common/commonActions";
import PagedList from "./PagedList";
import LoadingDialog from "../Dialogs/LoadingDialog";
import { OperationStatus } from "../../types/models";

interface DirectoryConfig {
  id: DropDownCategoryEnumModel;
  label: string;
  key: string;
  searchFn(
    pageIndex: number,
    pageSize: number,
    keyword: string,
  ): Promise<IPagedResultOfLookupItemModel>;
}

const NamesDirectoryManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedTab, setSelectedTab] = useState(0);
  const [refreshKeys, setRefreshKeys] = useState<Record<number, number>>({});
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set([0])); // Track which tabs have been loaded
  const {
    addCategoryVariableState,
    updateCategoryVariableState,
    deleteCategoryVariableState,
  } = useSelector((state: RootState) => state.commonData);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDropdownId, setEditingDropdownId] = useState<
    DropDownCategoryEnumModel | undefined
  >();
  const [editingOptionId, setEditingOptionId] = useState<number | undefined>();
  const [editingOptionName, setEditingOptionName] = useState("");

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    dropdownId: DropDownCategoryEnumModel;
    optionId: number;
    optionName: string;
  } | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingDropdownId, setAddingDropdownId] = useState<
    DropDownCategoryEnumModel | undefined
  >();
  const [newOptionValue, setNewOptionValue] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error" as "error" | "warning" | "info" | "success",
  });

  const { userData } = useSelector((state: RootState) => state.commonData);

  const directories: DirectoryConfig[] = [
    {
      id: DropDownCategoryEnumModel.Clients,
      label: "Clients",
      key: "clients",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchClients(pageIndex, pageSize, keyword);
      },
    },

    {
      id: DropDownCategoryEnumModel.SoiReferrer,
      label: "SOI Referrers",
      key: "soiReferrer",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchReferrers(
          ReferrerTypeEnumModel.Soi,
          pageIndex,
          pageSize,
          keyword,
        );
      },
    },
    {
      id: DropDownCategoryEnumModel.Vendor,
      label: "Vendors",
      key: "vendors",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchVendors(pageIndex, pageSize, keyword);
      },
    },
    {
      id: DropDownCategoryEnumModel.VendorReferrer,
      label: "Vendor Referrers",
      key: "vendorReferrer",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchReferrers(
          ReferrerTypeEnumModel.Vendor,
          pageIndex,
          pageSize,
          keyword,
        );
      },
    },
    {
      id: DropDownCategoryEnumModel.AgentReferrer,
      label: "Agent Referrers",
      key: "agentReferrer",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchReferrers(
          ReferrerTypeEnumModel.Agent,
          pageIndex,
          pageSize,
          keyword,
        );
      },
    },
    {
      id: DropDownCategoryEnumModel.AgentReferred,
      label: "Agents Referred",
      key: "agentReferred",
      async searchFn(pageIndex, pageSize, keyword) {
        return await searchReferrers(
          ReferrerTypeEnumModel.ReferredOut,
          pageIndex,
          pageSize,
          keyword,
        );
      },
    },
    {
      id: DropDownCategoryEnumModel.CoAgent,
      label: "Team Member/Co-Agents",
      key: "coAgent",
      async searchFn(pageIndex, pageSize, keyword) {
        // Co-Agent uses non-paged data from userData
        const coAgents = userData.data?.coAgents ?? [];
        const filtered = keyword
          ? coAgents.filter((item) =>
              item.name?.toLowerCase().includes(keyword.toLowerCase()),
            )
          : coAgents;
        const start = (pageIndex - 1) * pageSize;
        const end = start + pageSize;
        const paged = filtered.slice(start, end);
        return {
          result: paged,
          count: filtered.length,
          currentPage: pageIndex,
          pageSize: pageSize,
          pageCount: Math.ceil(filtered.length / pageSize),
        };
      },
    },
  ];

  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );

  const visibleDirectories = directories.filter(
    (directory) => !hiddenFields.some((field) => field.name === directory.key),
  );

  const handleTabChange = (newTab: number) => {
    setSelectedTab(newTab);
    // Mark this tab as loaded
    setLoadedTabs((prev) => new Set(prev).add(newTab));
  };

  const handleAddOption = (directory: DirectoryConfig, value: string) => {
    if (!value?.trim()) return;
    dispatch(addCategoryVariable(directory.id, value.trim()));
  };

  const handleEditOption = (
    dropdownId: DropDownCategoryEnumModel,
    option: ILookupItemModel,
  ) => {
    setEditingDropdownId(dropdownId);
    setEditingOptionId(option.id!);
    setEditingOptionName(option.name || "");
    setEditDialogOpen(true);
  };

  const handleSaveEditDialog = (newName: string) => {
    dispatch(
      updateCategoryVariable(editingDropdownId!, editingOptionId!, newName),
    );
    setEditDialogOpen(false);
    setEditingOptionId(undefined);
    setEditingDropdownId(undefined);
    setEditingOptionName("");
  };

  const handleDeleteOption = (
    dropdownId: DropDownCategoryEnumModel,
    optionId: number,
    optionName: string,
  ) => {
    setPendingDelete({ dropdownId, optionId, optionName });
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = () => {
    dispatch(
      deleteCategoryVariable(
        pendingDelete!.dropdownId,
        pendingDelete!.optionId,
      ),
    );
    setDeleteConfirmationOpen(false);
    setPendingDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (deleteCategoryVariableState.status === OperationStatus.Failed) {
      setSnackbar({
        open: true,
        message: `${
          deleteCategoryVariableState.message || "Failed to delete entry."
        }`,
        severity: "error",
      });
    } else if (
      deleteCategoryVariableState.status === OperationStatus.Completed &&
      deleteCategoryVariableState.category !== undefined
    ) {
      // Only refresh the specific category that was deleted from
      setRefreshKeys((prev) => ({
        ...prev,
        [deleteCategoryVariableState.category!]:
          (prev[deleteCategoryVariableState.category!] || 0) + 1,
      }));
    }
  }, [deleteCategoryVariableState]);

  useEffect(() => {
    if (addCategoryVariableState.error) {
      setSnackbar({
        open: true,
        message: `${addCategoryVariableState.error || "Failed to add entry."}`,
        severity: "error",
      });
    } else if (
      addCategoryVariableState.fulfilled &&
      addCategoryVariableState.category !== undefined
    ) {
      // Only refresh the specific category that was added to
      setRefreshKeys((prev) => ({
        ...prev,
        [addCategoryVariableState.category!]:
          (prev[addCategoryVariableState.category!] || 0) + 1,
      }));
    }
  }, [addCategoryVariableState]);

  useEffect(() => {
    if (
      updateCategoryVariableState.status === OperationStatus.Completed &&
      updateCategoryVariableState.category !== undefined
    ) {
      // Only refresh the specific category that was updated
      setRefreshKeys((prev) => ({
        ...prev,
        [updateCategoryVariableState.category!]:
          (prev[updateCategoryVariableState.category!] || 0) + 1,
      }));
    }
  }, [updateCategoryVariableState]);

  return (
    <>
      <LoadingDialog
        open={
          updateCategoryVariableState.status === OperationStatus.Pending ||
          deleteCategoryVariableState.status === OperationStatus.Pending ||
          addCategoryVariableState.pending
        }
      />
      <Box sx={{ width: "100%", p: 0 }}>
        <Box
          sx={{
            background: "#F7F9FB",
            borderRadius: "8px",
            py: 1,
            mb: 3,
            display: "flex",
            alignItems: "center",
            minHeight: 56,
          }}
        >
          {/* Tabs for medium and up screens */}
          <Box sx={{ display: { xs: "none", md: "block" }, width: "100%" }}>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={selectedTab}
              onChange={(e, newValue) => handleTabChange(newValue)}
              sx={{
                minHeight: 40,
                "& .MuiTabs-flexContainer": { gap: 0 },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  minHeight: 40,
                  borderRadius: "8px 8px 0 0",
                  color: "#222",
                  background: "transparent",
                  "&.Mui-selected": {
                    background: "#fff",
                    color: "primary.dark",
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {visibleDirectories.map((directory, index) => (
                <Tab label={directory.label} key={index} />
              ))}
            </Tabs>
          </Box>

          {/* Dropdown for small screens */}
          <Box
            sx={{ display: { xs: "block", md: "none" }, width: "100%", px: 2 }}
          >
            <FormControl fullWidth>
              <InputLabel id="directory-options-label">
                Select Directory
              </InputLabel>
              <Select
                labelId="directory-options-label"
                value={selectedTab}
                onChange={(e) => handleTabChange(Number(e.target.value))}
                label="Select Directory"
                sx={{
                  fontWeight: 600,
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {visibleDirectories.map((directory, index) => (
                  <MenuItem value={index} key={index}>
                    {directory.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Main Content */}
        {visibleDirectories.map((directory, index) => (
          <div hidden={selectedTab !== index} key={directory.id}>
            {loadedTabs.has(index) ? (
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "12px",
                  p: { xs: 2, md: 4 },
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                  mb: 1,
                  minHeight: 400,
                }}
              >
                {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    borderRadius: "8px",
                  }}
                >
                  <Button
                    disabled={addCategoryVariableState.pending}
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setAddingDropdownId(directory.id);
                      setNewOptionValue("");
                      setAddDialogOpen(true);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                      background: "primary.main",
                      borderRadius: "6px",
                      px: 2,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    Option
                  </Button>
                </Box> */}

                <PagedList
                  key={`${directory.id}-${refreshKeys[directory.id] || 0}`}
                  category={directory.id}
                  items={[]}
                  editedLabel={""}
                  onEditLabelChange={function (value: string): void {}}
                  onSaveEdit={function (optionId: number): void {}}
                  onEditOption={(option) => {
                    handleEditOption(directory.id, option);
                  }}
                  onDeleteOption={(opt) => {
                    handleDeleteOption(directory.id, opt.id!, opt.name!);
                  }}
                  allowPaging={true}
                  searchFn={directory.searchFn}
                />

                {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    justifyContent: "flex-end",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Button
                    disabled={addCategoryVariableState.pending}
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setAddingDropdownId(directory.id);
                      setNewOptionValue("");
                      setAddDialogOpen(true);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                      background: "primary.main",
                      borderRadius: "6px",
                      px: 2,
                      py: 1,
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    Add Option
                  </Button>
                </Box> */}
              </Box>
            ) : (
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "12px",
                  p: { xs: 2, md: 4 },
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                  mb: 1,
                  minHeight: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* This will never show since we load on tab selection */}
              </Box>
            )}
          </div>
        ))}
      </Box>

      <EditOptionDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveEditDialog}
        optionName={editingOptionName}
      />

      <EditOptionDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={(name) => {
          if (addingDropdownId !== undefined) {
            handleAddOption(
              visibleDirectories.find((d) => d.id === addingDropdownId)!,
              name,
            );
          }
          setAddDialogOpen(false);
          setAddingDropdownId(undefined);
          setNewOptionValue("");
        }}
        optionName={newOptionValue}
        dialogTitle="Add New Option"
      />

      <DeleteConfirmationDialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onConfirm={confirmDelete}
        optionName={pendingDelete?.optionName || ""}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NamesDirectoryManager;
